import React from "react";
import { motion } from "framer-motion";

// MATERIAL UI COMPONENTS
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import { alpha, useTheme } from "@mui/material/styles";

// ICONS
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import FastfoodIcon from "@mui/icons-material/Fastfood";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import { formatFinance } from "../../../utils/formatters";

// Animation variants
const itemVariants = {
  hidden: { y: 10, opacity: 0 },
  show: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 24,
    },
  },
};

const buttonVariants = {
  hover: { scale: 1.05 },
  tap: { scale: 0.95 },
};

const MenuProductRow = ({
  index,
  product,
  deleteAction,
  editAction,
  isAvailable,
  toggleAvailable,
}) => {
  const theme = useTheme();

  const handleRowClick = (event) => {
    const isActionButton = event.target.closest("button");

    if (isActionButton) {
      return;
    }

    editAction();
  };

  return (
    <Paper
      elevation={0}
      component={motion.div}
      variants={itemVariants}
      onClick={handleRowClick}
      whileHover={{
        y: -2,
        boxShadow: theme.shadows[2],
        backgroundColor: alpha(theme.palette.background.paper, 0.9),
      }}
      sx={{
        mb: 2,
        borderRadius: 2,
        backgroundColor: theme.palette.grey[50],
        overflow: "hidden",
        border: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`,
        transition: "all 0.2s ease-in-out",
        cursor: "pointer",
      }}
    >
      <Box sx={{ px: 2, py: 1.5 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
        >
          {/* Left side - Product info */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{ flex: 1 }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
                borderRadius: 1.5,
                p: 0.75,
                minWidth: 36,
                height: 36,
              }}
            >
              <FastfoodIcon fontSize="small" />
            </Box>

            <Stack direction="column" spacing={0.5}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="body1" fontWeight={600}>
                  {product.title}
                </Typography>
                <Chip
                  size="small"
                  label={`#${index}`}
                  sx={{
                    height: 22,
                    fontSize: "0.7rem",
                    bgcolor: alpha(theme.palette.grey[500], 0.1),
                    color: theme.palette.text.secondary,
                  }}
                />
              </Stack>

              <Stack direction="row" alignItems="center" spacing={1}>
                <Chip
                  icon={<AttachMoneyIcon sx={{ fontSize: 14 }} />}
                  label={formatFinance(parseFloat(product.price))}
                  size="small"
                  sx={{
                    height: 24,
                    bgcolor: alpha(theme.palette.success.main, 0.1),
                    color: theme.palette.success.main,
                    fontWeight: 500,
                    "& .MuiChip-icon": {
                      color: theme.palette.success.main,
                    },
                  }}
                />

                {product.categories?.map((category, i) => (
                  <motion.div
                    key={`category-${i}`}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: i * 0.05 }}
                  >
                    <Chip
                      icon={<LocalOfferIcon sx={{ fontSize: 14 }} />}
                      label={category.title}
                      size="small"
                      sx={{
                        height: 24,
                        bgcolor: alpha(theme.palette.info.main, 0.1),
                        color: theme.palette.info.main,
                        "& .MuiChip-icon": {
                          color: theme.palette.info.main,
                        },
                      }}
                    />
                  </motion.div>
                ))}
              </Stack>
            </Stack>
          </Stack>

          {/* Right side - Status and actions */}
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <motion.div
              variants={buttonVariants}
              whileHover="hover"
              whileTap="tap"
            >
              <Button
                size="small"
                onClick={toggleAvailable}
                startIcon={isAvailable ? <PauseIcon /> : <PlayArrowIcon />}
                sx={{
                  px: 2,
                  py: 0.6,
                  borderRadius: 6,
                  color: isAvailable
                    ? theme.palette.success.main
                    : theme.palette.error.main,
                  bgcolor: isAvailable
                    ? alpha(theme.palette.success.main, 0.1)
                    : alpha(theme.palette.error.main, 0.1),
                  "&:hover": {
                    bgcolor: isAvailable
                      ? alpha(theme.palette.success.main, 0.2)
                      : alpha(theme.palette.error.main, 0.2),
                  },
                  fontWeight: 600,
                  fontSize: "0.75rem",
                  letterSpacing: 0.5,
                }}
              >
                {isAvailable ? "DISPONÍVEL" : "PAUSADO"}
              </Button>
            </motion.div>

            <Divider orientation="vertical" flexItem sx={{ height: 24 }} />

            <Stack direction="row" spacing={0.5}>
              <motion.div
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                <Tooltip title="Editar Produto">
                  <IconButton
                    size="small"
                    onClick={editAction}
                    sx={{
                      color: theme.palette.primary.main,
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      p: 0.75,
                      "&:hover": {
                        bgcolor: alpha(theme.palette.primary.main, 0.2),
                      },
                    }}
                  >
                    <EditIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              </motion.div>

              <motion.div
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
              >
                <Tooltip title="Remover Produto">
                  <IconButton
                    size="small"
                    onClick={deleteAction}
                    sx={{
                      color: theme.palette.error.main,
                      bgcolor: alpha(theme.palette.error.main, 0.1),
                      p: 0.75,
                      "&:hover": {
                        bgcolor: alpha(theme.palette.error.main, 0.2),
                      },
                    }}
                  >
                    <DeleteIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              </motion.div>
            </Stack>
          </Stack>
        </Stack>
      </Box>
    </Paper>
  );
};

export const MenuProductList = ({ children }) => {
  return (
    <motion.div
      initial="hidden"
      animate="show"
      variants={{
        hidden: { opacity: 0 },
        show: {
          opacity: 1,
          transition: {
            staggerChildren: 0.1,
          },
        },
      }}
    >
      {children}
    </motion.div>
  );
};

export default MenuProductRow;
